// TelcoTrack — ROZVADEC welding phase (after Zafouknutý). One milestone: cassettes + direct welds.

import { getPhaseName, PHASE_CODE } from './phase-codes';
import { specFieldLabelCs } from './spec-field-registry';
import { isSamePhase } from './workflow-model';
import { MILESTONE_REVERT_OWN_WINDOW_MS } from './budova-milestone-engine';

/** Canonical welding milestone (commit/API use uppercase tokens). */
export const ROZVADEC_MILESTONE_IDS = ['PROVARENI'] as const;
export type RozvadecMilestoneId = (typeof ROZVADEC_MILESTONE_IDS)[number];

/** Pre-unification IDs — accepted on API / photos / history, normalized to `PROVARENI`. */
export const ROZVADEC_LEGACY_MILESTONE_IDS = [
  'PROVARENI_PRIMARNIHO_OK',
  'PROVARENI_SEKUNDARNICH_OK',
] as const;
export type RozvadecLegacyMilestoneId = (typeof ROZVADEC_LEGACY_MILESTONE_IDS)[number];

export const ROZVADEC_MILESTONE_API_IDS = [
  ...ROZVADEC_MILESTONE_IDS,
  ...ROZVADEC_LEGACY_MILESTONE_IDS,
] as const;

export const ROZVADEC_MILESTONE_LABELS: Record<RozvadecMilestoneId, string> = {
  PROVARENI: 'Provaření',
};

export const ROZVADEC_REQUIRED_MILESTONES: readonly RozvadecMilestoneId[] = [
  ...ROZVADEC_MILESTONE_IDS,
];

export const ROZVADEC_METADATA_KEYS = {
  rozvadecMilestones: 'rozvadecMilestones',
} as const;

/** `metadata.rozvadecMilestones` flag shape. Legacy dual flags still parsed. */
export interface RozvadecMilestonesFlags {
  provareni?: boolean;
  /** @deprecated dual-milestone leftover — complete only when both true */
  provareniPrimarnihoOk?: boolean;
  provareniSekundarnichOk?: boolean;
}

const MILESTONE_FLAG_KEY: Record<RozvadecMilestoneId, keyof RozvadecMilestonesFlags> = {
  PROVARENI: 'provareni',
};

const MILESTONE_ID_FROM_FLAG_KEY: Record<keyof RozvadecMilestonesFlags, RozvadecMilestoneId> = {
  provareni: 'PROVARENI',
  provareniPrimarnihoOk: 'PROVARENI',
  provareniSekundarnichOk: 'PROVARENI',
};

export function normalizeRozvadecMilestoneId(
  value: string | null | undefined,
): RozvadecMilestoneId | null {
  const id = (value ?? '').trim();
  if (id === 'PROVARENI') return 'PROVARENI';
  if ((ROZVADEC_LEGACY_MILESTONE_IDS as readonly string[]).includes(id)) return 'PROVARENI';
  return null;
}

function readRawRozvadecMilestonesObjectInternal(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!metadata || typeof metadata !== 'object') return {};
  const custom = metadata['customFields'];
  const fromCustom =
    custom && typeof custom === 'object' && !Array.isArray(custom)
      ? (custom as Record<string, unknown>)['rozvadecMilestones']
      : undefined;
  const raw = metadata['rozvadecMilestones'] ?? fromCustom;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
}

function parseFlag(raw: unknown): boolean {
  return raw === true || raw === 'true';
}

function isLegacyPairComplete(flags: RozvadecMilestonesFlags | null | undefined): boolean {
  return flags?.provareniPrimarnihoOk === true && flags?.provareniSekundarnichOk === true;
}

export function parseRozvadecMilestonesFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): RozvadecMilestonesFlags {
  const raw = readRawRozvadecMilestonesObjectInternal(metadata);
  const provareniPrimarnihoOk =
    parseFlag(raw['provareniPrimarnihoOk']) || parseFlag(raw['PROVARENI_PRIMARNIHO_OK']);
  const provareniSekundarnichOk =
    parseFlag(raw['provareniSekundarnichOk']) || parseFlag(raw['PROVARENI_SEKUNDARNICH_OK']);
  const provareni =
    parseFlag(raw['provareni']) ||
    parseFlag(raw['PROVARENI']) ||
    (provareniPrimarnihoOk && provareniSekundarnichOk);
  return { provareni, provareniPrimarnihoOk, provareniSekundarnichOk };
}

export function isRozvadecWeldingComplete(
  flags: RozvadecMilestonesFlags | null | undefined,
): boolean {
  return flags?.provareni === true || isLegacyPairComplete(flags);
}

export function isRozvadecMilestoneComplete(
  milestoneId: RozvadecMilestoneId | string,
  flags: RozvadecMilestonesFlags | null | undefined,
): boolean {
  if (!normalizeRozvadecMilestoneId(milestoneId)) return false;
  return isRozvadecWeldingComplete(flags);
}

export function allRozvadecMilestonesComplete(
  flags: RozvadecMilestonesFlags | null | undefined,
): boolean {
  return isRozvadecWeldingComplete(flags);
}

/**
 * Compact provaření picker replaces the full popup body (which hosts linear Zpět).
 * Keep the same previous-phase gate as TRASA / SPOJKOVISTE / full popup.
 */
export function shouldShowRozvadecMilestonePickerPhaseBack(params: {
  isMilestonePicker: boolean;
  canEditPreviousPhase: boolean;
  previousPhaseCount: number;
}): boolean {
  return (
    params.isMilestonePicker &&
    params.canEditPreviousPhase &&
    params.previousPhaseCount > 0
  );
}

/** Welding is a linear phase — never auto-enter a milestone picker/editor. */
export function shouldAutoEnterSingleOpenRozvadecMilestone(
  _openCount: number,
  _completedVisibleCount?: number,
): boolean {
  return false;
}

/** Legacy helper — welding is no longer a parallel milestone. Always false. */
export function hasPendingRozvadecWeldingMilestones(
  _currentStatus?: string | null,
  _flags?: RozvadecMilestonesFlags | null,
): boolean {
  return false;
}

/** Milestones run on Zafouknutý primární OK (`ROZVADEC.1.PHASE.2`). */
export function isRozvadecMilestoneHostPhase(currentStatus: string | null | undefined): boolean {
  return isSamePhase(currentStatus, PHASE_CODE.ROZVADEC_ZAFOUKNUTO_SLOUPEK);
}

/** Linear Zafouknutý → Provařené — do not hide next phases behind milestones. */
export function shouldFilterEditableNextPhasesForRozvadecMilestones(_params: {
  objectType: string | null | undefined;
  currentStatus: string | null | undefined;
}): boolean {
  return false;
}

export function resolveRozvadecAutoPhaseAfterAllMilestones(params: {
  currentStatus: string | null | undefined;
  milestones?: RozvadecMilestonesFlags | null;
}): string | null {
  const current = params.currentStatus ?? '';
  if (!isRozvadecMilestoneHostPhase(current)) return null;
  if (!allRozvadecMilestonesComplete(params.milestones)) return null;
  const target = PHASE_CODE.ROZVADEC_PROVARENE;
  return target?.trim() ? target : null;
}

export function buildRozvadecLinearNextPhaseAfterMilestones(params: {
  currentStatus: string | null | undefined;
  milestones?: RozvadecMilestonesFlags | null;
}): { code: string; name: string } | null {
  const code = resolveRozvadecAutoPhaseAfterAllMilestones(params);
  if (!code) return null;
  return { code, name: getPhaseName(code) };
}

/** API / history record: milestone id → boolean. */
export function rozvadecMilestonesRecord(flags: RozvadecMilestonesFlags): Record<string, boolean> {
  const done = isRozvadecWeldingComplete(flags);
  return {
    PROVARENI: done,
    PROVARENI_PRIMARNIHO_OK: done,
    PROVARENI_SEKUNDARNICH_OK: done,
  };
}

export function rozvadecMilestonesFlagsFromRecord(
  record: Record<string, boolean> | null | undefined,
): RozvadecMilestonesFlags {
  const provareni =
    record?.['PROVARENI'] === true ||
    (record?.['PROVARENI_PRIMARNIHO_OK'] === true &&
      record?.['PROVARENI_SEKUNDARNICH_OK'] === true);
  return {
    provareni,
    provareniPrimarnihoOk: record?.['PROVARENI_PRIMARNIHO_OK'] === true || provareni,
    provareniSekundarnichOk: record?.['PROVARENI_SEKUNDARNICH_OK'] === true || provareni,
  };
}

/** Only expose auto-advance when the welding phase is complete (client + API guard). */
export function resolveRozvadecAutoPhaseCodeForClient(params: {
  autoPhaseCode?: string | null;
  milestones?: Record<string, boolean> | null;
}): string | undefined {
  const code = params.autoPhaseCode?.trim();
  if (!code) return undefined;
  const flags = rozvadecMilestonesFlagsFromRecord(params.milestones ?? undefined);
  return allRozvadecMilestonesComplete(flags) ? code : undefined;
}

export function buildRozvadecMilestoneCompleteMetaPatch(params: {
  existingRaw?: Record<string, unknown>;
  flags: RozvadecMilestonesFlags;
  milestoneId: RozvadecMilestoneId | string;
  completedAtIso: string;
  userEmail: string;
}): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(params.existingRaw ?? {}) };
  const id = normalizeRozvadecMilestoneId(params.milestoneId) ?? 'PROVARENI';
  const email = params.userEmail.trim();
  for (const flagKey of ['provareni', 'provareniPrimarnihoOk', 'provareniSekundarnichOk'] as const) {
    next[flagKey] = true;
    next[`${flagKey}At`] = params.completedAtIso;
    next[`${flagKey}By`] = email;
  }
  next['PROVARENI'] = true;
  next['PROVARENI_PRIMARNIHO_OK'] = true;
  next['PROVARENI_SEKUNDARNICH_OK'] = true;
  next['lastCompletedMilestoneId'] = id;
  next['lastCompletedAt'] = params.completedAtIso;
  return next;
}

/** Extended milestone metadata (flags + per-milestone At/By audit). */
export interface RozvadecMilestonesMeta {
  flags: RozvadecMilestonesFlags;
  completedAt: Partial<Record<RozvadecMilestoneId, string>>;
  completedBy: Partial<Record<RozvadecMilestoneId, string>>;
  lastCompletedMilestoneId?: RozvadecMilestoneId;
  lastCompletedAt?: string;
}

/** Raw `rozvadecMilestones` object from metadata (top-level or customFields mirror). */
export function readRawRozvadecMilestonesObject(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return readRawRozvadecMilestonesObjectInternal(metadata);
}

/** Parse flags + completion audit (`provareni*OkAt`/`By`, lastCompleted*). */
export function parseRozvadecMilestonesMeta(
  metadata: Record<string, unknown> | null | undefined,
): RozvadecMilestonesMeta {
  const raw = readRawRozvadecMilestonesObjectInternal(metadata);
  const flags = parseRozvadecMilestonesFromMetadata(metadata);
  const completedAt: Partial<Record<RozvadecMilestoneId, string>> = {};
  const completedBy: Partial<Record<RozvadecMilestoneId, string>> = {};
  const atKeys = ['provareniAt', 'provareniPrimarnihoOkAt', 'provareniSekundarnichOkAt'] as const;
  const byKeys = ['provareniBy', 'provareniPrimarnihoOkBy', 'provareniSekundarnichOkBy'] as const;
  let bestAt = '';
  let bestBy = '';
  let bestTs = Number.NEGATIVE_INFINITY;
  atKeys.forEach((key, index) => {
    const at = raw[key];
    const by = raw[byKeys[index]];
    if (typeof at !== 'string' || !at.trim()) return;
    const ts = Date.parse(at);
    if (!Number.isFinite(ts) || ts < bestTs) return;
    bestTs = ts;
    bestAt = at.trim();
    bestBy = typeof by === 'string' ? by.trim() : '';
  });
  if (bestAt) completedAt.PROVARENI = bestAt;
  if (bestBy) completedBy.PROVARENI = bestBy;
  const lastId = raw['lastCompletedMilestoneId'];
  const lastCompletedMilestoneId =
    typeof lastId === 'string' ? normalizeRozvadecMilestoneId(lastId) ?? undefined : undefined;
  const lastAt = raw['lastCompletedAt'];
  return {
    flags,
    completedAt,
    completedBy,
    lastCompletedMilestoneId,
    lastCompletedAt: typeof lastAt === 'string' && lastAt.trim() ? lastAt.trim() : undefined,
  };
}

const ROZVADEC_MILESTONE_REVERT_ORDER: readonly RozvadecMilestoneId[] = ['PROVARENI'];

function rozvadecMilestoneRevertOrderIndex(id: RozvadecMilestoneId): number {
  const i = ROZVADEC_MILESTONE_REVERT_ORDER.indexOf(id);
  return i >= 0 ? i : ROZVADEC_MILESTONE_REVERT_ORDER.length;
}

/** Last completed welding milestone by `lastCompletedMilestoneId` or max At timestamp. */
export function resolveLastCompletedRozvadecMilestone(
  meta: RozvadecMilestonesMeta,
): RozvadecMilestoneId | null {
  const completedIds = ROZVADEC_REQUIRED_MILESTONES.filter((id) =>
    isRozvadecMilestoneComplete(id, meta.flags),
  );
  if (!completedIds.length) return null;

  if (meta.lastCompletedMilestoneId && completedIds.includes(meta.lastCompletedMilestoneId)) {
    return meta.lastCompletedMilestoneId;
  }

  let best: RozvadecMilestoneId | null = null;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const id of completedIds) {
    const at = meta.completedAt[id];
    const ts = at ? Date.parse(at) : Number.NaN;
    if (!Number.isFinite(ts)) continue;
    if (
      best == null ||
      ts > bestTs ||
      (ts === bestTs &&
        rozvadecMilestoneRevertOrderIndex(id) > rozvadecMilestoneRevertOrderIndex(best))
    ) {
      best = id;
      bestTs = ts;
    }
  }
  if (best) return best;

  let fallback: RozvadecMilestoneId | null = null;
  for (const id of completedIds) {
    if (
      fallback == null ||
      rozvadecMilestoneRevertOrderIndex(id) > rozvadecMilestoneRevertOrderIndex(fallback)
    ) {
      fallback = id;
    }
  }
  return fallback;
}

/**
 * Completer within 1h (or admin/manager) may uncomplete a welding milestone.
 * Window SSOT: `MILESTONE_REVERT_OWN_WINDOW_MS` (same as BUDOVA).
 */
export function canRevertRozvadecMilestone(params: {
  milestoneId: RozvadecMilestoneId | string;
  meta: RozvadecMilestonesMeta;
  userEmail: string | null | undefined;
  isAdmin: boolean;
  now?: number;
}): boolean {
  const { meta, userEmail, isAdmin } = params;
  const milestoneId = normalizeRozvadecMilestoneId(params.milestoneId);
  if (!milestoneId || !isRozvadecMilestoneComplete(milestoneId, meta.flags)) return false;
  if (isAdmin) return true;
  const completedAt = meta.completedAt[milestoneId];
  const completedBy = meta.completedBy[milestoneId];
  if (!completedAt || !completedBy || !userEmail?.trim()) return false;
  if (userEmail.trim().toLowerCase() !== completedBy.trim().toLowerCase()) return false;
  const ts = Date.parse(completedAt);
  if (!Number.isFinite(ts)) return false;
  const now = params.now ?? Date.now();
  return now - ts <= MILESTONE_REVERT_OWN_WINDOW_MS;
}

/** Clear milestone flag + At/By after revert; recompute lastCompleted*. */
export function buildRozvadecMilestoneRevertMetaPatch(params: {
  existingRaw: Record<string, unknown>;
  flags: RozvadecMilestonesFlags;
  milestoneId: RozvadecMilestoneId | string;
}): Record<string, unknown> {
  const next: Record<string, unknown> = { ...params.existingRaw };
  for (const flagKey of ['provareni', 'provareniPrimarnihoOk', 'provareniSekundarnichOk'] as const) {
    next[flagKey] = false;
    delete next[`${flagKey}At`];
    delete next[`${flagKey}By`];
  }
  delete next['PROVARENI'];
  delete next['PROVARENI_PRIMARNIHO_OK'];
  delete next['PROVARENI_SEKUNDARNICH_OK'];

  const remaining = ROZVADEC_REQUIRED_MILESTONES.filter((id) =>
    isRozvadecMilestoneComplete(id, params.flags),
  );
  let lastCompletedMilestoneId: RozvadecMilestoneId | undefined;
  let lastCompletedAt: string | undefined;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const id of remaining) {
    const key = MILESTONE_FLAG_KEY[id];
    const atRaw = next[`${key}At`];
    const at = typeof atRaw === 'string' ? atRaw.trim() : '';
    const ts = at ? Date.parse(at) : Number.NaN;
    if (!Number.isFinite(ts)) continue;
    if (
      !lastCompletedMilestoneId ||
      ts > bestTs ||
      (ts === bestTs &&
        rozvadecMilestoneRevertOrderIndex(id) >
          rozvadecMilestoneRevertOrderIndex(lastCompletedMilestoneId))
    ) {
      lastCompletedMilestoneId = id;
      lastCompletedAt = at;
      bestTs = ts;
    }
  }
  if (lastCompletedMilestoneId) {
    next['lastCompletedMilestoneId'] = lastCompletedMilestoneId;
    if (lastCompletedAt) next['lastCompletedAt'] = lastCompletedAt;
    else delete next['lastCompletedAt'];
  } else {
    delete next['lastCompletedMilestoneId'];
    delete next['lastCompletedAt'];
  }
  return next;
}

/**
 * When uncompleting a welding milestone clears the auto Provařené gate while still on
 * Provařené, roll status back to Zafouknutý (host).
 */
export function shouldRollbackStatusOnRozvadecMilestoneRevert(
  currentStatus: string | null | undefined,
  flagsAfterRevert: RozvadecMilestonesFlags,
): string | null {
  if (allRozvadecMilestonesComplete(flagsAfterRevert)) return null;
  if (!isSamePhase(currentStatus, PHASE_CODE.ROZVADEC_PROVARENE)) return null;
  return PHASE_CODE.ROZVADEC_ZAFOUKNUTO_SLOUPEK;
}

/**
 * Backward phase Provařené → Zafouknutý must uncomplete the last welding milestone,
 * otherwise all gates stay satisfied and the object advances forward again.
 */
export function isRozvadecPhaseRevertToMilestoneHost(
  currentStatus: string | null | undefined,
  targetStatus: string | null | undefined,
): boolean {
  return (
    isSamePhase(currentStatus, PHASE_CODE.ROZVADEC_PROVARENE) &&
    isRozvadecMilestoneHostPhase(targetStatus)
  );
}

export function sumRozvadecWeldCounts(
  primary: number | null | undefined,
  secondary: number | null | undefined,
): number | null {
  const p = primary != null && Number.isFinite(primary) && primary >= 0 ? Math.floor(primary) : null;
  const s =
    secondary != null && Number.isFinite(secondary) && secondary >= 0 ? Math.floor(secondary) : null;
  if (p == null && s == null) return null;
  return (p ?? 0) + (s ?? 0);
}

export function resolveRozvadecWeldCountColumn(
  _milestoneId?: RozvadecMilestoneId | string,
): 'weldCountPrimary' | 'weldCountSecondary' {
  return 'weldCountPrimary';
}

export type RozvadecWeldCountInfoRow = {
  label: string;
  value: string;
};

/** INFORMACE rows for persisted ROZVADEC welding counts (unique welds when known). */
export function buildRozvadecWeldCountInfoRows(
  primary: number | null | undefined,
  secondary: number | null | undefined,
  weldCountTotal?: number | null,
): readonly RozvadecWeldCountInfoRow[] {
  const billed =
    weldCountTotal != null && Number.isFinite(weldCountTotal) && weldCountTotal >= 0
      ? Math.floor(weldCountTotal)
      : sumRozvadecWeldCounts(primary, secondary);
  if (billed == null) return [];
  return [{ label: specFieldLabelCs('WELD_COUNT'), value: String(billed) }];
}

/**
 * INFORMACE Provaření — occupancy / weld-layout total only.
 * Never session `weldCountSvaru` or stale object columns.
 * `0` is a real loaded count; omit the row when occupancy is unknown.
 */
export function buildRozvadecOccupancyWeldInfoRows(
  occupancyWeldCount: number | null | undefined,
): readonly RozvadecWeldCountInfoRow[] {
  if (occupancyWeldCount == null || !Number.isFinite(occupancyWeldCount) || occupancyWeldCount < 0) {
    return [];
  }
  return [{ label: specFieldLabelCs('WELD_COUNT'), value: String(Math.floor(occupancyWeldCount)) }];
}

export function isRozvadecMilestoneId(value: string): value is RozvadecMilestoneId {
  return normalizeRozvadecMilestoneId(value) !== null;
}

export function rozvadecMilestoneIdFromFlagKey(
  key: keyof RozvadecMilestonesFlags,
): RozvadecMilestoneId {
  return MILESTONE_ID_FROM_FLAG_KEY[key];
}
